import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Cpu, Eye, Handshake } from "lucide-react";

const CODE_BG = "https://images.unsplash.com/photo-1590010358311-55d7c0769a3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxjb2RlJTIwb24lMjBzY3JlZW4lMjBkYXJrfGVufDB8fHxibGFja3wxNzc0NTA0OTc0fDA&ixlib=rb-4.1.0&q=85";

const reasons = [
  {
    icon: Cpu,
    title: "Equipo especializado",
    description: "En desarrollo de soluciones a medida, cloud y arquitectura de datos.",
  },
  {
    icon: Eye,
    title: "Procesos transparentes",
    description: "Pensados para que entiendas cada etapa del proyecto, sin jerga innecesaria.",
  },
  {
    icon: Handshake,
    title: "Acompañamiento completo",
    description: "Desde la definición de la idea hasta el despliegue de la solución.",
  },
];

const WhyZulunitySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="por-que"
      data-testid="why-section"
      ref={ref}
      className="py-24 sm:py-32 relative overflow-hidden"
      style={{ backgroundColor: "#050505" }}
    >
      {/* Subtle bg image */}
      <div className="absolute inset-0 opacity-[0.04]">
        <img src={CODE_BG} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left: text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-mono tracking-[0.25em] uppercase text-neutral-500 block mb-4">
              Nosotros
            </span>
            <h2
              data-testid="why-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-8"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              ¿Por qué hacer tu desarrollo gratis con Zulunity?
            </h2>
            <p className="text-base sm:text-lg text-neutral-400 leading-relaxed">
              En Zulunity utilizamos la innovación, la creatividad y la adaptabilidad
              para crear soluciones tecnológicas personalizadas para tu empresa. Actuamos
              como tu brazo derecho tecnológico, combinando desarrollo de software, cloud
              y datos para llevar tus ideas a producción.
            </p>
          </motion.div>

          {/* Right: reasons */}
          <div className="space-y-6">
            {reasons.map((reason, i) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 * (i + 1) }}
                className="group flex gap-5 p-6 border border-white/[0.06] hover:border-white/20 bg-[#0A0A0A]/50 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-white/10 bg-white/[0.03]">
                  <reason.icon className="w-5 h-5 text-white opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: "'Syne', sans-serif" }}>
                    {reason.title}
                  </h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyZulunitySection;
